import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  RedirectCommand,
  ResolveFn,
  Router,
} from '@angular/router';
import { catchError, map, of } from 'rxjs';

import {
  AuthService,
  MfaContextResponse,
  MyLabsResponse,
} from '../../features/auth/services/auth.service';
import { CHALLENGE_TIMED_OUT } from '../../features/auth/services/sign-in-notices';

/*
 * Route-level checks for the pages that need a signed-in session (or, for the
 * two-factor step, a live challenge). Each runs before its page is created, so
 * nobody who is not signed in sees any of it — not even for a moment.
 *
 * sso-be's "not signed in" answers (TERMS_REQUIRED, PASSWORD_EXPIRED) never
 * reach these: TermsRequiredInterceptor signs out and goes to the sign-in page
 * with the reason, and that navigation replaces the one waiting here.
 */

/** No session at all: the sign-in page. Anything else: let the page show its own error. */
function onSessionError(err: unknown, router: Router) {
  return err instanceof HttpErrorResponse && err.status === 401
    ? new RedirectCommand(router.parseUrl('/login'))
    : null;
}

/** The workspace list, fetched before the picker is shown. */
export const labsResolver: ResolveFn<MyLabsResponse | null> = () => {
  const router = inject(Router);
  return inject(AuthService)
    .myLabs()
    .pipe(catchError((err) => of(onSessionError(err, router))));
};

/**
 * The security page needs a signed-in session, or a Lab's handoff ticket —
 * the page exchanges that ticket for a session itself.
 */
export const securityPageGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  if (route.queryParamMap.get('ticket')) return true;
  const router = inject(Router);
  return inject(AuthService)
    .myLabs()
    .pipe(
      map(() => true),
      catchError((err) => of(onSessionError(err, router) ?? true)),
    );
};

/**
 * The two-factor step needs a live challenge; without one, start sign-in again.
 * In place of the dead step, so Back does not lead straight back to it.
 */
export const mfaContextResolver: ResolveFn<MfaContextResponse | RedirectCommand> = () => {
  const router = inject(Router);
  return inject(AuthService)
    .mfaContext()
    .pipe(
      catchError(() =>
        of(
          new RedirectCommand(router.parseUrl('/login'), {
            state: { notice: CHALLENGE_TIMED_OUT },
            replaceUrl: true,
          }),
        ),
      ),
    );
};
